package com.arena_web.ui.sections.home.mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arena_web.ui.components.overlay.SupportPopup
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.header.components.BrandLogo
import com.arena_web.ui.sections.home.ArrowUpSvg
import com.arena_web.ui.sections.home.HeroBurstKeyframes
import com.arena_web.ui.sections.home.HeroChunkKeyframes
import com.arena_web.ui.sections.home.HeroLogoIntroKeyframes
import com.arena_web.ui.sections.home.HeroSportWords
import com.arena_web.ui.sections.home.HeroWordSwapKeyframes
import com.arena_web.ui.sections.home.HomeAmbientOrbs
import com.arena_web.ui.sections.home.attachTilt
import com.arena_web.ui.sections.home.injectArrowNudgeStyles
import com.arena_web.ui.sections.home.revealOnScroll
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList

private const val URBANIST = AppTypography.PRIMARY_FONT_NAME
private const val URBANIST_FB = AppTypography.PRIMARY_FONT_FALLBACK

// Figma 9781:247386 — full mobile main page (390 × 2796).
// Top bar + safe-area chrome live in MobilePageLayout; this composable owns the
// scrollable content column under it.
@Composable
fun HomeContentMobile() {
    val t = LocalAppStrings.current
    // Soft blue orbs drift behind the content (fixed full-viewport layer,
    // z-index 0). Shared with the desktop showcase.
    HomeAmbientOrbs()
    // MobilePageLayout paints the #02040B surface for the whole layout (chrome
    // included) — this column just hosts the scrollable content. Lifted above
    // the orb layer with `position: relative` + z-index 1.
    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "relative")
                property("z-index", "1")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
    ) {
        MobileHero()
        MobileSpacer(28)
        MobileSection(
            title = t.homeSectionCreateEventsTitle,
            highlightWord = t.homeSectionCreateEventsHighlight,
            subtitleLines = listOf(
                t.homeSectionCreateEventsSubtitle1,
                t.homeSectionCreateEventsSubtitle2,
            ),
            cards = listOf(
                MobileCarouselCard("/images/home/mobile/arena-events.png", t.homeAltEventsScreen),
                MobileCarouselCard("/images/home/mobile/create-event.png", t.homeAltCreateEventScreen),
                MobileCarouselCard("/images/home/mobile/event-detail.png", t.homeAltEventDetailScreen),
            ),
            // Rotations mirror HomeShowcase desktop section 1: -45 (↖) at the
            // top, 45 (↗) at the bottom.
            arrows = listOf(
                MobileArrowSpec(rotateDeg = -45, vAlign = ArrowVAlign.Top),
                MobileArrowSpec(rotateDeg = 45, vAlign = ArrowVAlign.Bottom),
            ),
        )
        MobileSpacer(40)
        MobileSection(
            title = t.homeSectionTeammatesTitle,
            highlightWord = t.homeSectionTeammatesHighlight,
            subtitleLines = emptyList(),
            cards = listOf(
                MobileCarouselCard("/images/home/mobile/discover-people.png", t.homeAltDiscoverPeople),
                MobileCarouselCard("/images/home/mobile/matched-interests.png", t.homeAltMatchedInterests),
                MobileCarouselCard("/images/home/mobile/match-popup.png", t.homeAltItsAMatch),
            ),
            // Rotations mirror HomeShowcase desktop section 2: both arrows are
            // 90° (→, pointing right) and ride the top of the row.
            arrows = listOf(
                MobileArrowSpec(rotateDeg = 90, vAlign = ArrowVAlign.Top),
                MobileArrowSpec(rotateDeg = 90, vAlign = ArrowVAlign.Top),
            ),
        )
        MobileSpacer(40)
        MobileSection(
            title = t.homeSectionMomentsTitle,
            highlightWord = t.homeSectionMomentsHighlight,
            subtitleLines = emptyList(),
            cards = listOf(
                MobileCarouselCard("/images/home/mobile/feed.png", t.homeAltFeedScreen),
                MobileCarouselCard("/images/home/mobile/feed-post.png", t.homeAltFeedPost),
                MobileCarouselCard("/images/home/mobile/chat.png", t.homeAltChatScreen),
            ),
            // Rotations mirror HomeShowcase desktop section 3: 45 (↗) at the
            // bottom of the feed gap, 90 (→) at the top between post and chat.
            arrows = listOf(
                MobileArrowSpec(rotateDeg = 45, vAlign = ArrowVAlign.Bottom),
                MobileArrowSpec(rotateDeg = 90, vAlign = ArrowVAlign.Top),
            ),
        )
        MobileSpacer(40)
        MobileSection(
            title = t.homeSectionStatsTitle,
            highlightWord = t.homeSectionStatsHighlight,
            subtitleLines = emptyList(),
            cards = listOf(
                MobileCarouselCard("/images/home/mobile/profile-overview.png", t.homeAltProfileOverview),
                MobileCarouselCard(
                    "/images/home/mobile/player-stats.png",
                    t.homeAltPlayerStatsRadar,
                    label = t.homeLabelPlayerStats,
                ),
                MobileCarouselCard(
                    "/images/home/mobile/rate-performance.png",
                    t.homeAltRatePerformance,
                    label = t.homeLabelRatePerformance,
                    // Rate Performance is the widest of the three callout
                    // cards in Figma 9806:403685 and is nudged right of the
                    // shorter Player Stats radar so the row reads as a stagger.
                    widthPx = 160,
                    offsetLeftPx = 4,
                ),
            ),
            // Rotations mirror HomeShowcase desktop section 4: 90 (→) rides
            // the bottom between profile and stats, 135 (↘) sits at the top
            // between stats and rate-performance.
            arrows = listOf(
                MobileArrowSpec(rotateDeg = 90, vAlign = ArrowVAlign.Bottom),
                MobileArrowSpec(rotateDeg = 135, vAlign = ArrowVAlign.Top),
            ),
        )
        MobileSpacer(56)
        MobileFooter()
        // Clear the fixed bottom navigation bar — BottomNavBar height ≈ 96 px
        // at the 390-px reference plus a 24 px breathing margin.
        MobileSpacer(120)
    }
}

@Composable
private fun MobileSpacer(px: Int) {
    Box(modifier = Modifier.mHeight(px))
}

// =============================================================================
// Hero — multi-line tagline with pill chips on key words + store badges
// =============================================================================

// Tagline chunks start fading in just before the intro logo lands at the
// MobileTopBar — matches HomeShowcase's `HERO_TAGLINE_START_MS` pattern
// (`HeroIntroTiming.TOTAL_MS - 200`).
private const val MOBILE_TAGLINE_START_MS = HeroIntroTiming.TOTAL_MS - 200

@Composable
private fun MobileHero() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .mPaddingTop(16)
            .mPaddingLeft(16)
            .mPaddingRight(16)
            .mGap(16),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Logo + tagline share the same vertical slot. The logo is absolutely
        // positioned (centered) so it overlays the tagline during intro; the
        // tagline itself stays invisible until ~MOBILE_TAGLINE_START_MS, then
        // fades in chunk-by-chunk as the logo flies up to the MobileTopBar.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("display", "flex")
                    property("justify-content", "center")
                    property("align-items", "center")
                }
        ) {
            // Inline-flowing tagline. Word order mirrors the desktop
            // HeroTagline exactly (Arena Pro Play / unites / [athletes] /
            // [and teams] / worldwide for / [every game]).
            // Semantically the page's H1 (all chunks set explicit font
            // size/weight, so only the UA margin needs resetting).
            H1(
                attrs = {
                    style {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("justify-content", "center")
                        property("align-items", "center")
                        property("text-align", "center")
                        property("column-gap", m(8))
                        property("row-gap", m(8))
                        property("width", "100%")
                        property("margin", "0")
                    }
                }
            ) {
                HeroChunk(MOBILE_TAGLINE_START_MS)        { HeroDisplay(t.homeHeroBrand, color = "#0061FF", bold = true, sizePx = 26) }
                HeroChunk(MOBILE_TAGLINE_START_MS + 300)  { HeroDisplay(t.homeHeroUnites, color = "#BED6F5", bold = false, sizePx = 22) }
                HeroChunk(MOBILE_TAGLINE_START_MS + 600)  { HeroPill(t.homeHeroAthletes, fontPx = 14, padPx = 14) }
                HeroChunk(MOBILE_TAGLINE_START_MS + 900)  { HeroPill(t.homeHeroAndTeams, fontPx = 14, padPx = 14) }
                HeroChunk(MOBILE_TAGLINE_START_MS + 1200) { HeroDisplay(t.homeHeroWorldwideFor, color = "#BED6F5", bold = false, sizePx = 18) }
                HeroChunk(MOBILE_TAGLINE_START_MS + 1500) { MobileRotatingSportPill() }
            }
            // Big logo sits absolutely centered over the tagline. It assembles,
            // pauses, then a FLIP clone flies up into the MobileTopBar's brand
            // glyph — at which point the tagline (which has been fading in)
            // is the only thing left in this slot.
            MobileHeroIntroLogo()
        }

        // Store badges appear after the tagline finishes typing in.
        Row(
            modifier = Modifier
                .mGap(12)
                .mPaddingTop(8)
                .animation(
                    HeroChunkKeyframes.toAnimation(
                        duration = 500.ms,
                        delay = (MOBILE_TAGLINE_START_MS + 1800).ms,
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Both,
                    )
                ),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MobileAppleStoreButton()
            MobileGooglePlayButton()
        }
    }
}

// =============================================================================
// MobileHeroIntroLogo — the big logo intro for mobile.
// Mirrors HomeShowcase's HeroIntroLogo + HeroLogoFlight pair: the SVG assembles
// arc by arc (`mhero-seg` keyframes), a halo ring bursts outward, then a fixed-
// position clone flies into MobileTopBar's brand glyph (data-brand-glyph).
// Sizes are scaled for the 390-px mobile baseline via `m()`.
// =============================================================================

private const val MOBILE_HERO_LOGO_ID = "mobile-hero-intro-logo"
private const val MOBILE_HERO_GHOST_ID = "mobile-hero-intro-logo-ghost"

@Composable
private fun MobileHeroIntroLogo() {
    // Absolutely positioned so the logo overlays the tagline slot and takes
    // no flow space — when it flies up to MobileTopBar the tagline is what
    // remains.
    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "absolute")
                property("left", "50%")
                // Logo center sits below the slot's middle line so the
                // pop-in animation drops it just under the tagline anchor —
                // matches the Figma 9806:403692 hero composition.
                property("top", "70%")
                property("transform", "translate(-50%, -50%)")
                property("width", m(110))
                property("height", m(75))
                property("pointer-events", "none")
                property("z-index", "5")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Halo burst — expanding ring that flashes at the moment the
        // assembly completes, then dissolves outward.
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "50%")
                    property("transform", "translate(-50%, -50%)")
                    property("width", m(140))
                    property("height", m(140))
                    property("border-radius", "50%")
                    property("border", "2px solid rgba(0, 97, 255, 0.5)")
                }
                .animation(
                    HeroBurstKeyframes.toAnimation(
                        duration = 850.ms,
                        delay = (HeroIntroTiming.INTRO_MS - 280).ms,
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Both,
                    )
                )
        )
        // The logo container scales from ~half size to full while the SVG
        // segments individually sweep into place.
        Box(
            modifier = Modifier
                .id(MOBILE_HERO_LOGO_ID)
                .animation(
                    HeroLogoIntroKeyframes.toAnimation(
                        duration = HeroIntroTiming.INTRO_MS.ms,
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Both,
                    )
                )
        ) {
            Div(
                attrs = {
                    style {
                        property("width", m(110))
                        property("height", m(75))
                        property("display", "block")
                    }
                    attr("role", "img")
                    attr("aria-label", "Arena logo")
                    ref { el ->
                        injectMobileHeroSegmentStyles()
                        window.fetch("/icons/ic_proplay_logo_glow.svg")
                            .then { it.text() }
                            .then { svgText ->
                                el.innerHTML = svgText.unsafeCast<String>()
                                // Counterclockwise from the top-left arc with the
                                // inner arc landing last — same order as desktop.
                                val assemblyOrder = listOf(3, 2, 4, 1, 5, 6)
                                el.querySelectorAll("g[filter]").asList()
                                    .filterIsInstance<Element>()
                                    .forEachIndexed { i, g ->
                                        g.setAttribute(
                                            "class",
                                            "mhero-seg mhero-seg-${assemblyOrder.getOrElse(i) { 6 }}"
                                        )
                                    }
                            }
                        onDispose { }
                    }
                }
            )
        }
    }
    MobileHeroLogoFlight()
}

// Stylesheet for the mobile logo's segment sweep — scoped to
// #mobile-hero-intro-logo so the desktop hero's identical selectors don't
// double-bind. Each segment rotates ~18° inward while fading in.
private fun injectMobileHeroSegmentStyles() {
    val styleId = "mobile-hero-logo-segment-styles"
    if (document.getElementById(styleId) != null) return
    val stagger = 100
    val style = document.createElement("style")
    style.id = styleId
    style.textContent = buildString {
        appendLine(
            """
            @keyframes mhero-seg-sweep {
                from { opacity: 0; transform: rotate(18deg); }
                to { opacity: 1; transform: rotate(0deg); }
            }
            #$MOBILE_HERO_LOGO_ID .mhero-seg {
                opacity: 0;
                transform-box: view-box;
                transform-origin: 50% 50%;
                animation: mhero-seg-sweep 320ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
            }
            """.trimIndent()
        )
        repeat(6) { i ->
            appendLine("#$MOBILE_HERO_LOGO_ID .mhero-seg-${i + 1} { animation-delay: ${40 + i * stagger}ms; }")
        }
    }
    document.head?.appendChild(style)
}

// FLIP hand-off: clone the assembled logo, fix it to its current viewport
// rect, then translate+scale it onto MobileTopBar's brand glyph so the header
// glyph can dock in place without a visible seam.
@Composable
private fun MobileHeroLogoFlight() {
    DisposableEffect(Unit) {
        val launch: () -> Unit = launch@{
            val logo = document.getElementById(MOBILE_HERO_LOGO_ID) as? HTMLElement ?: return@launch
            val from = logo.getBoundingClientRect()
            val glyph = document.querySelectorAll("[data-brand-glyph]").asList()
                .filterIsInstance<HTMLElement>()
                .firstOrNull { it.getBoundingClientRect().width > 0.0 }
            val ghost = logo.cloneNode(true) as HTMLElement
            // The segment-pop CSS is scoped to #mobile-hero-intro-logo; under
            // a different id the cloned SVG renders fully assembled — exactly
            // the state the flight needs.
            ghost.id = MOBILE_HERO_GHOST_ID
            ghost.style.animation = "none"
            ghost.style.position = "fixed"
            ghost.style.left = "${from.left}px"
            ghost.style.top = "${from.top}px"
            ghost.style.width = "${from.width}px"
            ghost.style.height = "${from.height}px"
            ghost.style.margin = "0"
            ghost.style.zIndex = "10000"
            ghost.style.setProperty("pointer-events", "none")
            ghost.style.transform = "none"
            ghost.style.transformOrigin = "top left"
            document.body?.appendChild(ghost)
            logo.style.visibility = "hidden"
            // Reading the rect flushes the ghost's starting styles, arming the transition.
            ghost.getBoundingClientRect()
            ghost.style.transition =
                "transform ${HeroIntroTiming.FLY_MS}ms cubic-bezier(0.6, -0.15, 0.25, 1), " +
                    "opacity ${HeroIntroTiming.FLY_MS}ms ease-in"
            if (glyph != null) {
                val to = glyph.getBoundingClientRect()
                val scale = to.width / from.width
                ghost.style.transform =
                    "translate(${to.left - from.left}px, ${to.top - from.top}px) scale($scale)"
            } else {
                ghost.style.transform = "translate(0px, -30vh) scale(0.2)"
                ghost.style.opacity = "0"
            }
        }
        val removeGhost = {
            document.getElementById(MOBILE_HERO_GHOST_ID)?.let { it.parentElement?.removeChild(it) }
        }
        val settle: () -> Unit = settle@{
            // Clearing the segment animations once assembly is done re-renders the
            // logo as crisp vector instead of GPU-promoted layers.
            val logo = document.getElementById(MOBILE_HERO_LOGO_ID) as? HTMLElement ?: return@settle
            logo.querySelectorAll(".mhero-seg").asList()
                .filterIsInstance<Element>()
                .forEach {
                    it.setAttribute("style", "animation: none; opacity: 1; transform: none")
                }
            logo.style.animation = "none"
        }
        val settleTimer = window.setTimeout(settle, HeroIntroTiming.INTRO_MS + 100)
        val flyTimer = window.setTimeout(launch, HeroIntroTiming.INTRO_MS + HeroIntroTiming.HOLD_MS)
        val swapTimer = window.setTimeout({ removeGhost() }, HeroIntroTiming.TOTAL_MS + 80)
        onDispose {
            window.clearTimeout(settleTimer)
            window.clearTimeout(flyTimer)
            window.clearTimeout(swapTimer)
            removeGhost()
        }
    }
}

// Wraps a tagline chunk with the shared fade-in keyframes plus a per-chunk
// delay. `display: inline-flex` is required because the Div parent is a
// flex-wrap container — without it the wrapper would line-break awkwardly.
@Composable
private fun HeroChunk(delayMs: Int, content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .styleModifier { property("display", "inline-flex") }
            .animation(
                HeroChunkKeyframes.toAnimation(
                    duration = 500.ms,
                    delay = delayMs.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                    fillMode = AnimationFillMode.Both,
                )
            )
    ) { content() }
}

// Mobile twin of HomeShowcase's `HeroRotatingSportPill` — every 2.2 s the pill
// remounts to a new sport and the word swap re-runs `HeroWordSwapKeyframes`
// (rise-in from below).
//
// Timing mirrors the desktop: chunk arrival (`MOBILE_TAGLINE_START_MS + 1500`)
// + chunk fade-in (~500 ms) + 3 s read-time on "every game" before the first
// rotation. After that, 2.2 s between swaps.
@Composable
private fun MobileRotatingSportPill() {
    var rotations by remember { mutableStateOf(0) }
    LaunchedEffect(Unit) {
        kotlinx.coroutines.delay((MOBILE_TAGLINE_START_MS + 1500 + 500 + 3000).toLong())
        while (true) {
            rotations++
            kotlinx.coroutines.delay(2200)
        }
    }
    // Fixed-width slot keeps the surrounding tagline words anchored — without
    // it, each swap from a short word ("padel") to a long one ("basketball")
    // reshapes the flex-wrap row and the words to the left jump horizontally.
    Box(
        modifier = Modifier.styleModifier {
            property("display", "flex")
            property("justify-content", "center")
            property("min-width", m(MOBILE_ROTATING_PILL_MIN_WIDTH))
            property("flex-shrink", "0")
        }
    ) {
        key(rotations) {
            val word = HeroSportWords[rotations % HeroSportWords.size]
            val textModifier = if (rotations == 0) Modifier else Modifier.animation(
                HeroWordSwapKeyframes.toAnimation(
                    duration = 350.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                    fillMode = AnimationFillMode.Both,
                )
            )
            HeroPill(word, fontPx = 14, padPx = 14, textModifier = textModifier)
        }
    }
}

// Picked to fit the widest entry in `HeroSportWords` ("basketball" /
// "volleyball" / "every game") at fontPx=14 plus the 14-px side padding and a
// ~2-px breathing room. Bump this if a longer sport is ever added.
private const val MOBILE_ROTATING_PILL_MIN_WIDTH = 120

@Composable
private fun HeroDisplay(text: String, color: String, bold: Boolean, sizePx: Int) {
    SpanText(
        text,
        modifier = Modifier
            .mFontSize(sizePx)
            .mLineHeight(sizePx + 4)
            .mLetterSpacing(sizePx * 0.02)
            .fontWeight(if (bold) AppTypography.Bold else AppTypography.Medium)
            .fontFamily(URBANIST, URBANIST_FB)
            .styleModifier {
                property("color", color)
                property("white-space", "nowrap")
            }
    )
}

@Composable
private fun HeroPill(text: String, fontPx: Int, padPx: Int, textModifier: Modifier = Modifier) {
    Box(
        modifier = Modifier
            .mHeight(24)
            .mBorderRadius(100)
            .mPaddingLeft(padPx)
            .mPaddingRight(padPx)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("position", "relative")
                property("flex-shrink", "0")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary}, rgba(8, 12, 28, 0.55)"
                )
                property("backdrop-filter", "blur(6px)")
                property("-webkit-backdrop-filter", "blur(6px)")
            }
    ) {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(fontPx)
                .mLineHeight(fontPx + 4)
                .mLetterSpacing(fontPx * 0.02)
                .fontWeight(AppTypography.Medium)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier {
                    property("color", "#BED6F5")
                    property("white-space", "nowrap")
                }
                .then(textModifier)
        )
        // Gradient ring border via mask-composite (project rule on borders).
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", "100px")
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
    }
}

// =============================================================================
// Section — centred headline with single-word underline + horizontal carousel
// =============================================================================

// [label] adds an optional pill above the mockup (e.g. "Player Stats",
// "Rate Performance" — Figma 9806:403685 / 10785:198248 callouts).
// [widthPx] overrides the default 120-px card width (Figma carousels keep most
// cards at ~120 px but some — like Rate Performance — are slightly wider).
// [offsetLeftPx] nudges this card right of the default flex position (used to
// recreate the staggered layout in the Track Stats row).
private data class MobileCarouselCard(
    val image: String,
    val alt: String,
    val label: String? = null,
    val widthPx: Int = 120,
    val offsetLeftPx: Int = 0,
)

private enum class ArrowVAlign { Top, Center, Bottom }

// Per-arrow placement spec — one entry per gap (so for N cards we expect N-1).
// [rotateDeg] is applied to the base arrow-up glyph: 45° → ↗ (top-right),
// -45° → ↖ (top-left), 135° → ↘, etc.
private data class MobileArrowSpec(val rotateDeg: Int, val vAlign: ArrowVAlign)

@Composable
private fun MobileSection(
    title: String,
    highlightWord: String,
    subtitleLines: List<String>,
    cards: List<MobileCarouselCard>,
    arrows: List<MobileArrowSpec> = emptyList(),
) {
    Column(
        modifier = Modifier.fillMaxWidth().mGap(16),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Section title fades + slides up the first time the heading scrolls
        // into view (same `revealOnScroll` rig as HomeShowcase desktop).
        Box(modifier = Modifier.fillMaxWidth().revealOnScroll(0)) {
            SectionTitle(title, highlightWord)
        }
        if (subtitleLines.isNotEmpty()) {
            Column(
                modifier = Modifier.fillMaxWidth().mGap(2).revealOnScroll(100),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                subtitleLines.forEach { line ->
                    SpanText(
                        line,
                        modifier = Modifier
                            .mFontSize(13)
                            .mLineHeight(18)
                            .mLetterSpacing(0.26)
                            .fontWeight(AppTypography.Regular)
                            .color(AppColors.Text.Secondary)
                            .fontFamily(URBANIST, URBANIST_FB)
                            .styleModifier { property("text-align", "center") }
                    )
                }
            }
        }
        MobileCarousel(cards, arrows)
    }
}

// Title with the chosen [highlightWord] underlined. Rendered as an inline flex
// row of words so the underline stays glued to the highlighted word at any
// wrap point.
@Composable
private fun SectionTitle(text: String, highlightWord: String) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("justify-content", "center")
                property("align-items", "baseline")
                property("text-align", "center")
                property("column-gap", m(6))
                property("row-gap", m(2))
                property("width", "100%")
                property("padding-left", m(16))
                property("padding-right", m(16))
            }
        }
    ) {
        text.split(" ").forEach { word ->
            // Stripped form for matching — highlight even when the word carries a
            // trailing comma (e.g. "Friends," vs "Friends").
            val isHighlight = word.trim(',', '.', '!').equals(highlightWord, ignoreCase = true)
            Box(
                modifier = Modifier.styleModifier {
                    property("display", "inline-flex")
                    property("flex-direction", "column")
                    property("align-items", "center")
                }
            ) {
                SpanText(
                    word,
                    modifier = Modifier
                        .mFontSize(20)
                        .mLineHeight(26)
                        .mLetterSpacing(0.4)
                        .fontWeight(AppTypography.SemiBold)
                        .color(AppColors.Text.Secondary)
                        .fontFamily(URBANIST, URBANIST_FB)
                )
                if (isHighlight) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .mHeight(1)
                            .styleModifier {
                                property("margin-top", m(2))
                                property(
                                    "background",
                                    "linear-gradient(90deg, rgba(60,121,241,0) 0%, rgba(60,121,241,0.8) 50%, rgba(60,121,241,0) 100%)"
                                )
                            }
                    )
                }
            }
        }
    }
}

// Horizontal carousel — peek-next card pattern. The first card is offset from
// the screen edge by the page gutter; subsequent cards are visible via
// horizontal scroll. Matches Figma 9818:417671 / 9786:146253 / 9795:305725 /
// 9806:403684 where carousel widths exceed the 358-wide container.
@Composable
private fun MobileCarousel(
    cards: List<MobileCarouselCard>,
    arrows: List<MobileArrowSpec> = emptyList(),
) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("flex-direction", "row")
                // Cross-axis stretch lets each arrow choose its own vertical
                // alignment via `align-self` (Top/Center/Bottom). Cards keep
                // their natural height because the inner img sizes the box.
                property("align-items", "stretch")
                property("gap", m(4))
                // Default left inset so the first card never hugs the screen
                // edge — matches the Figma 9818:417667 container start at x=16
                // plus a touch of breathing room. The right side stays open so
                // the next card peeks in and signals there is more to scroll.
                property("padding-left", m(16))
                property("padding-right", m(16))
                property("scroll-padding-left", m(16))
                property("width", "100%")
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
                property("scroll-snap-type", "x mandatory")
                property("-webkit-overflow-scrolling", "touch")
            }
        }
    ) {
        // Interleave: card, ↗, card, ↗, card … (no arrow after the last card).
        // The diagonal-up arrow is the same "lead-the-eye" pill the desktop
        // shows between mockups (HomeShowcase DiagonalArrowPill). Each arrow's
        // direction + vertical position comes from [arrows], matching Figma.
        // Each card and arrow rides in on `revealOnScroll` with a stagger so the
        // row composes itself the moment the carousel scrolls into view.
        cards.forEachIndexed { index, card ->
            MobileMockupCard(
                card,
                modifier = Modifier.revealOnScroll(100 + index * 150),
            )
            if (index < cards.lastIndex) {
                val spec = arrows.getOrNull(index)
                    ?: MobileArrowSpec(rotateDeg = 45, vAlign = ArrowVAlign.Center)
                MobileArrowPill(
                    spec,
                    modifier = Modifier.revealOnScroll(500 + index * 150, pop = true),
                    pulseDelayMs = index * 400,
                )
            }
        }
    }
}

// Diagonal-up arrow pill between cards — same lead-the-eye idea as the desktop
// `DiagonalArrowPill` (HomeShowcase 9654:135447 / 9680:174451). Sized down for
// 390-px reference. Rotation + vertical alignment come from the section's spec.
// Glyph carries the project's shared `arrow-nudge` keyframes (gentle recurring
// translateY along the arrow's own rotated frame) so each pill leads the eye
// through the row instead of sitting static.
@Composable
private fun MobileArrowPill(
    spec: MobileArrowSpec,
    modifier: Modifier = Modifier,
    pulseDelayMs: Int = 0,
) {
    val alignSelf = when (spec.vAlign) {
        ArrowVAlign.Top -> "flex-start"
        ArrowVAlign.Center -> "center"
        ArrowVAlign.Bottom -> "flex-end"
    }
    Box(
        modifier = modifier
            .mSize(34)
            .mBorderRadius(999)
            .styleModifier {
                property("flex-shrink", "0")
                property("align-self", alignSelf)
                property("background", AppGradients.InteractiveSecondary)
                property("backdrop-filter", "blur(2px)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("position", "relative")
            }
    ) {
        // The rotation lives in `--arrow-rot` rather than an inline transform:
        // the nudge keyframes must own `transform`, and an inline value would
        // override them. The style injection rides on this element's ref —
        // the pill root may already carry the scroll-reveal ref, and an element
        // only gets one ref.
        Box(
            modifier = Modifier
                .mSize(16)
                .classNames("arrow-nudge")
                .attrsModifier {
                    ref { _ ->
                        injectArrowNudgeStyles()
                        onDispose { }
                    }
                }
                .styleModifier {
                    property("background-image", "url(\"data:image/svg+xml;utf8,$ArrowUpSvg\")")
                    property("background-size", "contain")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                    property("--arrow-rot", "${spec.rotateDeg}deg")
                    property("animation-delay", "${pulseDelayMs}ms")
                }
        )
        // Gradient ring border via the project's mask-composite trick.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", "999px")
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
    }
}

@Composable
private fun MobileMockupCard(card: MobileCarouselCard, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .mWidth(card.widthPx)
            .mGap(8)
            .styleModifier {
                property("flex-shrink", "0")
                property("scroll-snap-align", "start")
                if (card.offsetLeftPx != 0) {
                    property("margin-left", m(card.offsetLeftPx))
                }
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (card.label != null) {
            MobileCardPillLabel(card.label)
        }
        // The card body owns the tilt — leans toward the cursor (mouse only,
        // touch devices never trigger it). The transition lives on this Box
        // instead of the outer Column because the parent already carries the
        // scroll-reveal class whose transition an inline transform would
        // override.
        Div(
            attrs = {
                style {
                    property("width", "100%")
                    property("box-shadow", "0 10px 24px rgba(0, 0, 0, 0.35)")
                    property("transition", "transform 180ms ease-out")
                }
                ref { el ->
                    val detach = attachTilt(el)
                    onDispose { detach() }
                }
            }
        ) {
            Img(
                src = card.image,
                alt = card.alt,
                attrs = {
                    style {
                        property("width", "100%")
                        property("height", "auto")
                        property("display", "block")
                    }
                },
            )
        }
    }
}

// Blue-gradient pill label that sits above a carousel card — Figma callouts
// for "Player Stats" / "Rate Performance" (9806:403685 / 10785:198248). Mirrors
// the desktop `PillLabel` style: vertical gradient bg, gradient ring border,
// soft backdrop blur.
@Composable
private fun MobileCardPillLabel(text: String) {
    // Same shape + sizing as the SegmentedControl border, just the colour comes
    // from the top stop of `AppGradients.ButtonBorderGradientPrimary` so the
    // stroke matches the project token without going through mask-composite.
    // `width: max-content` + `flex-shrink: 0` lets the pill grow past the
    // 120-px card column when the label text (e.g. "Rate Performance") is wider
    // than the mockup itself; without it, the flex column would squeeze the
    // pill down to the card width and clip the trailing characters.
    Box(
        modifier = Modifier
            .mBorderRadius(140)
            .mPadding(v = 10, h = 22)
            .styleModifier {
                property("box-sizing", "border-box")
                property("width", "max-content")
                property("max-width", "none")
                property("flex-shrink", "0")
                property(
                    "background",
                    "linear-gradient(0deg, rgba(12, 66, 255, 1) 3.12%, rgba(102, 102, 102, 0) 60.116%)"
                )
                property("backdrop-filter", "blur(3px)")
                property("-webkit-backdrop-filter", "blur(3px)")
            }
    ) {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(15)
                .fontWeight(AppTypography.Medium)
                .mLineHeight(18)
                .mLetterSpacing(0.15)
                .color(AppColors.Text.Secondary)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// =============================================================================
// Footer — brand, links, social, store buttons, copyright
// =============================================================================

@Composable
private fun MobileFooter() {
    val t = LocalAppStrings.current
    var showSupport by remember { mutableStateOf(false) }
    val ctx = rememberPageContext()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .mPaddingLeft(32)
            .mPaddingRight(32)
            .mGap(28),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Brand mark + tagline (Figma 9803:341755).
        Column(
            modifier = Modifier.mGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            BrandLogo(static = true)
            SpanText(
                t.homeFooterTagline,
                modifier = Modifier
                    .mFontSize(13)
                    .mLineHeight(18)
                    .mLetterSpacing(0.26)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Regular)
                    .styleModifier { property("text-align", "center") }
            )
            // Crawlable product blurb — mirrors the desktop footer copy.
            SpanText(
                t.homeFooterBlurb,
                modifier = Modifier
                    .mFontSize(12)
                    .mLineHeight(17)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Regular)
                    .styleModifier { property("text-align", "center") }
            )
        }

        // Stacked links.
        Column(
            modifier = Modifier.mGap(16),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            MobileFooterLink(t.homeFooterAbout, path = "/about")
            MobileFooterLink(t.homeFooterTerms, path = "/terms")
            MobileFooterLink(t.homeFooterPrivacy, path = "/privacy")
            MobileFooterLink(t.homeFooterSupports) { showSupport = true }
        }

        // Social icons row (Facebook, Instagram).
        Row(
            modifier = Modifier.mGap(12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MobileSocialIcon(MobileFacebookIconSvg)
            MobileSocialIcon(MobileInstagramIconSvg)
        }

        // Stacked store buttons (matches the Figma footer order — Apple on top).
        Column(
            modifier = Modifier.mGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            MobileAppleStoreButton()
            MobileGooglePlayButton()
        }

        SpanText(
            t.homeFooterCopyright,
            modifier = Modifier
                .mFontSize(11)
                .mLineHeight(14)
                .color(AppColors.Text.Secondary)
                .fontFamily(URBANIST, URBANIST_FB)
                .fontWeight(AppTypography.Regular)
        )
    }
    SupportPopup(isOpen = showSupport, onClose = { showSupport = false })
}

@Composable
private fun MobileFooterLink(text: String, path: String? = null, onClick: (() -> Unit)? = null) {
    val ctx = rememberPageContext()
    val label: @Composable () -> Unit = {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(15)
                .mLineHeight(20)
                .color(AppColors.Text.Secondary)
                .fontFamily(URBANIST, URBANIST_FB)
                .fontWeight(AppTypography.Regular)
                .cursor(Cursor.Pointer)
                .thenIf(onClick != null) { Modifier.onClick { onClick!!() } }
        )
    }
    if (path != null) {
        // Real <a href> so crawlers can follow internal links; navigation
        // itself stays client-side through the router.
        A(
            href = path,
            attrs = {
                style { property("text-decoration", "none") }
                onClick {
                    it.preventDefault()
                    ctx.router.navigateTo(path)
                }
            }
        ) { label() }
    } else {
        label()
    }
}

@Composable
private fun MobileSocialIcon(svg: String) {
    Box(
        modifier = Modifier
            .mSize(40)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", AppGradients.InteractiveSecondary)
                property("border", "0.5px solid rgba(255, 255, 255, 0.4)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        Box(
            modifier = Modifier
                .mSize(20)
                .styleModifier {
                    property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                    property("background-size", "contain")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                }
        )
    }
}

// =============================================================================
// Store buttons — white pill with stacked eyebrow + name + brand glyph
// =============================================================================

@Composable
private fun MobileAppleStoreButton() {
    val t = LocalAppStrings.current
    MobileStorePill(withShadow = true) {
        MobileStoreIcon(MobileAppleIconSvg)
        Column {
            SpanText(
                t.homeStoreAppleEyebrow,
                modifier = Modifier
                    .mFontSize(8)
                    .mLineHeight(10)
                    .mLetterSpacing(0.08)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Medium)
            )
            SpanText(
                t.homeStoreAppleName,
                modifier = Modifier
                    .mFontSize(14)
                    .mLineHeight(18)
                    .mLetterSpacing(0.07)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
        }
    }
}

@Composable
private fun MobileGooglePlayButton() {
    val t = LocalAppStrings.current
    MobileStorePill(withShadow = false) {
        MobileStoreIcon(MobileGooglePlayIconSvg)
        Column {
            SpanText(
                t.homeStoreGoogleEyebrow,
                modifier = Modifier
                    .mFontSize(8)
                    .mLineHeight(10)
                    .mLetterSpacing(0.08)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
            SpanText(
                t.homeStoreGoogleName,
                modifier = Modifier
                    .mFontSize(14)
                    .mLineHeight(18)
                    .mLetterSpacing(0.07)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
        }
    }
}

@Composable
private fun MobileStorePill(withShadow: Boolean, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .mWidth(152)
            .mHeight(44)
            .mBorderRadius(100)
            .backgroundColor(AppColors.PrimaryWhite)
            .mPaddingLeft(12)
            .mPaddingRight(12)
            .mGap(8)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.5px solid rgba(255, 255, 255, 0.4)")
                property("flex-shrink", "0")
                if (withShadow) property("box-shadow", "0 4px 2px rgba(0, 0, 0, 0.25)")
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center,
    ) {
        content()
    }
}

@Composable
private fun MobileStoreIcon(svg: String) {
    Box(
        modifier = Modifier
            .mSize(24)
            .styleModifier {
                property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                property("background-size", "contain")
                property("background-repeat", "no-repeat")
                property("background-position", "center")
            }
    )
}

// =============================================================================
// Inline SVG icons — URL-encoded for data: URI use.
// Same artwork as HomeShowcase, duplicated here so the mobile file is
// self-contained and doesn't depend on private members of the desktop file.
// =============================================================================

private val MobileAppleIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E" +
        "%3Cpath d='M17.564 13.234c-.026-2.6 2.122-3.847 2.22-3.91-1.21-1.77-3.094-2.012-3.764-2.04-1.602-.16-3.13.945-3.945.945-.814 0-2.072-.92-3.404-.895-1.752.026-3.368 1.018-4.27 2.586-1.82 3.158-.466 7.823 1.305 10.378.864 1.252 1.895 2.659 3.246 2.61 1.302-.052 1.794-.844 3.37-.844 1.575 0 2.018.844 3.396.815 1.404-.026 2.292-1.275 3.146-2.534.99-1.456 1.402-2.866 1.428-2.94-.032-.012-2.74-1.054-2.768-4.17zM14.943 5.4c.722-.873 1.207-2.087 1.075-3.293-1.04.042-2.297.69-3.043 1.562-.668.77-1.252 2.005-1.094 3.184 1.157.088 2.34-.586 3.062-1.453z'/%3E" +
        "%3C/svg%3E"

private val MobileGooglePlayIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E" +
        "%3Cpath fill='%2300C2FF' d='M3.46 2.46C3.18 2.72 3 3.09 3 3.5v17c0 .41.18.78.46 1.04L13.4 12 3.46 2.46z'/%3E" +
        "%3Cpath fill='%2300C853' d='M3.46 2.46l9.94 9.54 3.41-3.41L4.53 2.1c-.41-.06-.79.1-1.07.36z'/%3E" +
        "%3Cpath fill='%23FFC107' d='M16.81 8.59L13.4 12l3.41 3.41 4.04-2.27c.69-.39.69-1.39 0-1.78l-4.04-2.77z'/%3E" +
        "%3Cpath fill='%23EA4335' d='M3.46 21.54c.28.26.66.42 1.07.36l12.28-6.49L13.4 12 3.46 21.54z'/%3E" +
        "%3C/svg%3E"

private val MobileInstagramIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23B4CCF6' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Crect x='2' y='2' width='20' height='20' rx='5'/%3E" +
        "%3Cpath d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'/%3E" +
        "%3Cline x1='17.5' y1='6.5' x2='17.51' y2='6.5'/%3E" +
        "%3C/svg%3E"

private val MobileFacebookIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23B4CCF6'%3E" +
        "%3Cpath d='M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89H7.898v-2.89h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33V21.879C18.343 21.128 22 16.991 22 12z'/%3E" +
        "%3C/svg%3E"

